/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import PropTypes from 'prop-types'
import React, {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {FormattedMessage, useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'

import Seo from '@salesforce/retail-react-app/app/components/seo'
import {
    Box,
    Button,
    Container,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

import {useChangePasswordMutation} from '../../clients/ssr/hooks/use-reset-password'
import Link from '../../components/link'
import {colors} from '../../constants'
import ChangePasswordForm from './partials/change-password-form'

const ChangePassword = () => {
    const form = useForm()
    const navigate = useNavigation()
    const {formatMessage} = useIntl()
    const [showSubmittedSuccess, setShowSubmittedSuccess] = useState(false)
    const [token, setToken] = useState()
    const einstein = useEinstein()
    const {pathname} = useLocation()

    const changePasswordMutation = useChangePasswordMutation()

    const submitForm = async ({newPassword}) => {
        try {
            await changePasswordMutation.mutateAsync({newPassword, token})
            setShowSubmittedSuccess(!showSubmittedSuccess)
        } catch (error) {
            if (error?.message === 'invalid token') {
                form.setError('global', {
                    type: 'manual',
                    message: formatMessage({
                        id: 'reset_password.invalid_token',
                        defaultMessage: 'The email link is incorrect'
                    })
                })
            }
        }
    }

    /**************** Einstein ****************/
    useEffect(() => {
        einstein.sendViewPage(pathname)

        setToken(new URLSearchParams(window.location.search).get('token'))
    }, [])

    return (
        <Box data-testid="reset-password-page">
            <Seo
                title="Set New Password"
                description="Reset customer password"
            />
            <Container
                width={['100%', '528px']}
                padding={['12px', 0]}
                margin={['80px auto 171px', '100px auto 343px']}
            >
                {!showSubmittedSuccess ? (
                    <ChangePasswordForm
                        form={form}
                        submitForm={submitForm}
                        clickSignIn={() => navigate('/login')}
                    />
                ) : (
                    <Stack justify="center" align="center" marginBottom="40px">
                        <Text
                            align="center"
                            fontSize="40px"
                            lineHeight="47px"
                            fontFamily="Raleway"
                            fontWeight="300"
                        >
                            <FormattedMessage
                                defaultMessage={'Set New Password'}
                                id="reset_password.title.password_reset"
                            />
                        </Text>
                        <Stack
                            spacing={6}
                            pt={4}
                            alignItems="center"
                            width="100%"
                        >
                            <Text align="center" fontSize="sm">
                                <FormattedMessage
                                    defaultMessage="Password Changed Successfully"
                                    id="reset_password.success.message"
                                />
                            </Text>

                            <Button
                                onClick={() => form.clearErrors('global')}
                                width="100%"
                                margin="0px 2px"
                                borderRadius="20px"
                                display="flex"
                                gap="10px"
                                padding="0 14px"
                                justifyContent="left"
                                fontSize="14px"
                                fontWeight="300"
                                background={colors.black}
                                backgroundColor={colors.black}
                                _hover={{
                                    background: colors.black,
                                    backgroundColor: colors.black
                                }}
                                _active={{
                                    background: colors.black,
                                    backgroundColor: colors.black
                                }}
                                _disabled={{
                                    background: colors.black,
                                    backgroundColor: colors.black
                                }}
                            >
                                <Link
                                    buttonStyles={{
                                        color: colors.white,
                                        fontSize: '14px',
                                        fontWeight: '300',
                                        textDecoration: 'none',
                                        _hover: {
                                            textDecoration: 'none'
                                        },
                                        _focus: {
                                            background: colors.black,
                                            backgroundColor: colors.black
                                        },
                                        width: '100%',
                                        marginRight: '20px',
                                        background: colors.black,
                                        backgroundColor: colors.black,
                                        height: '15px'
                                    }}
                                    type="tertiary"
                                    text="Sign in"
                                    onLinkClick={() => navigate('/login')}
                                />
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </Container>
        </Box>
    )
}

ChangePassword.getTemplateName = () => 'change-password'

ChangePassword.propTypes = {
    match: PropTypes.object
}

export default ChangePassword
